import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { current_pin: "", new_pin: "", confirm_pin: "" };
const pinProps = {
    type: "password", inputMode: "numeric", pattern: "[0-9]*", maxLength: 4,
    autoComplete: "one-time-code",
};

/** Personal 4-digit Announcement PIN (hashed server-side, never retrievable). */
export const AnnouncementPinCard = () => {
    const [isSet, setIsSet] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/me/announcement-pin")
            .then(({ data }) => setIsSet(Boolean(data?.is_set)))
            .catch(() => setIsSet(false));
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value.replace(/\D/g, "") }));

    const submit = async (e) => {
        e.preventDefault();
        if (form.new_pin.length !== 4) return toast.error("Your PIN must be exactly 4 digits.");
        if (form.new_pin !== form.confirm_pin) return toast.error("The two PINs don't match.");
        setSaving(true);
        try {
            await api.post("/me/announcement-pin", {
                new_pin: form.new_pin,
                ...(isSet ? { current_pin: form.current_pin } : {}),
            });
            setForm(EMPTY);
            setIsSet(true);
            toast.success("Announcement PIN saved.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save PIN");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="border-[#E2E8F0]" data-testid="announcement-pin-card">
            <CardContent className="p-6 sm:p-8">
                <div className="flex items-start gap-2.5 mb-6">
                    <LockKey size={20} weight="duotone" className="text-[#0066D9] mt-0.5" />
                    <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-base md:text-lg font-semibold text-[#1F2A44]" style={{ fontFamily: "Outfit" }}>
                                Announcement PIN
                            </h2>
                            <Badge className={isSet
                                ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                                : "bg-amber-100 text-amber-800 border border-amber-200"}
                                data-testid="announcement-pin-status">
                                {isSet ? "PIN set" : "Not set"}
                            </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                            A 4-digit PIN that only you know — needed to open private announcements.
                            Nobody, including admins, can ever see it.
                        </p>
                    </div>
                </div>
                <form onSubmit={submit} className="space-y-4 max-w-xs">
                    {isSet && (
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Current PIN</Label>
                            <Input {...pinProps} required value={form.current_pin} onChange={set("current_pin")}
                                className="h-11 border-[#E2E8F0] tracking-[0.5em] text-center"
                                data-testid="current-pin-input" />
                        </div>
                    )}
                    <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                            {isSet ? "New PIN" : "Choose a PIN"}
                        </Label>
                        <Input {...pinProps} required value={form.new_pin} onChange={set("new_pin")}
                            className="h-11 border-[#E2E8F0] tracking-[0.5em] text-center"
                            data-testid="new-pin-input" />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Confirm PIN</Label>
                        <Input {...pinProps} required value={form.confirm_pin} onChange={set("confirm_pin")}
                            className="h-11 border-[#E2E8F0] tracking-[0.5em] text-center"
                            data-testid="confirm-pin-input" />
                    </div>
                    <Button type="submit" disabled={saving} className="bg-[#003366] hover:bg-[#0066D9] h-11"
                        data-testid="save-pin-button">
                        {saving ? "Saving…" : isSet ? "Change PIN" : "Set PIN"}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
};
